import { group as d3Group, hierarchy as d3Hierarchy } from 'd3';
import { Fragment, type ReactNode } from 'react';

import type { NodeEntity } from '@/components/CourseMap/coursemap.types';
import type { Details } from '@/stores/alignmentStore';

import { StyledCardDeck, StyledWrapper } from '../CourseAlignmentStyles';

import Accordion from './Accordion/Accordion';
import CourseCard from './CourseCard/CourseCard';
import DisciplineCard from './DisciplineCard';
import ListCard from './ListCard';

interface CardDeckProps {
	course: any;
	selectedDiscipline: Discipline[];
	coursesArray: any;
	selectedDivision: Division[];
	isAP: boolean;
	details: Details;
}

const CardDeck = ({ ...props }: CardDeckProps) => {
	const { coursesArray, selectedDivision, selectedDiscipline, course, isAP, details } = props;
	const nodes: any = d3Hierarchy(course);
	const group: any = d3Group(nodes.descendants(), (d: any) => d.depth);
	const setClassName = (c: any) => (c.data.name.includes('Spacer') ? 'spacer' : '').toString();

	const disciplineNodes: ReactNode[] = [];
	const courseNodes: ReactNode[] = [];

	Array.from(group).forEach((g: any, i: number) => {
		if (g[1].length > 1) {
			const childrenWithoutSpacer = g[1].filter((x: NodeEntity) => !x.data.name.includes('Spacer'));

			courseNodes.push(
				<Fragment key={`${course.id}-${course.tracker}-${i.toString()}`}>
					<ListCard details={details}>
						{childrenWithoutSpacer.map((c: any, index: number) => {
							const courseData = coursesArray?.find((a: any) => a.course_id === c.data.tracker);

							const isVisible =
								selectedDivision.find((d) => d.name === c.data.division)?.isSelected &&
								!c.data.isArchived;

							const isApOnly = /AP/.test(c.data.name);
							const uniqueKey = `${g[0]}-${index}-${c.data.tracker}`;

							if (isAP) {
								return (
									isApOnly && (
										<Fragment key={`list-ap-${uniqueKey}`}>
											<Accordion
												accordionKey={`list-ap-${uniqueKey}`}
												courseData={courseData}
												title={c.data.name}
												details={details}
											/>
										</Fragment>
									)
								);
							}

							if (!selectedDiscipline.find((d) => d.name === c.data.discipline)?.isSelected) {
								return null;
							}

							return (
								<Fragment key={`list-accordion-${uniqueKey}`}>
									{isVisible && (
										<Accordion
											courseData={courseData}
											title={c.data.name}
											details={details}
											accordionKey={`list-accordion-${uniqueKey}`}
										/>
									)}
								</Fragment>
							);
						})}
					</ListCard>
				</Fragment>
			);

			return;
		}

		const childrenWithoutSpacer = g[1].filter((x: NodeEntity) => !x.data.name.includes('Spacer'));

		childrenWithoutSpacer.forEach((c: any, index: number) => {
			if (!selectedDiscipline.find((d) => d.name === c.data.discipline)?.isSelected) {
				return;
			}

			const uniqueKey = `${g[0]}-${index}-${c.data.tracker}`;

			if (c.data?.isDiscipline) {
				disciplineNodes.push(
					<DisciplineCard name={c.data.name} key={`discipline-${uniqueKey}`} color={c.data.color} />
				);
				return;
			}

			const isApOnly = c.data.isAp;

			if (isAP) {
				if (isApOnly) {
					courseNodes.push(
						<Fragment key={`card-ap-${uniqueKey}`}>
							<CourseCard
								courseData={coursesArray?.find((a: any) => a.course_id === c.data.tracker)}
								className={setClassName(c)}
								data-id={g.depth}
								courseName={c.data.name}
								courseDivision={c.data.division}
								details={details}
							/>
						</Fragment>
					);
				}
				return;
			}

			const isNotVisible = selectedDivision.some(
				(d) => (d.name === c.data.division && !d.isSelected) || c.data.isArchived
			);
			if (isNotVisible) return;

			courseNodes.push(
				<Fragment key={`card-${uniqueKey}`}>
					<CourseCard
						courseData={coursesArray?.find((a: any) => a.course_id === c.data.tracker)}
						className={setClassName(c)}
						data-id={g.depth}
						courseName={c.data.name}
						courseDivision={c.data.division}
						details={details}
					/>
				</Fragment>
			);
		});
	});

	return (
		<StyledCardDeck className={`${course.name?.replace(/\s/g, '-')}-row deck`}>
			{disciplineNodes}
			<StyledWrapper>{courseNodes}</StyledWrapper>
		</StyledCardDeck>
	);
};

export default CardDeck;
